import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LinkIcon from '@mui/icons-material/Link'
import SearchIcon from '@mui/icons-material/Search'

import PageHeader from '../../components/system/PageHeader'
import { BRAND_GRADIENT } from '../../components/social-media/design-tokens'
import CampaignFilter from '../../components/social-media/CampaignFilter'
import EntityEditPanel from '../../components/social-media/EntityEditPanel'
import { SectionHeader } from '../../components/social-media'
import useEntityFilterSummary from '../../hooks/useEntityFilterSummary'
import {
  buildEntitySearchQuery,
  useEntityDetail,
  useEntityPatch,
  useEntitySearch,
} from '../../hooks/socialdash/useEntityEditor'
import ProductTabs, { ADMIN_TAB } from './ProductTabs'
import PostDataCsvActions from './PostDataCsvActions'
import AdminViewTabs from './AdminViewTabs'
import { SKY_SOCIAL_CUSTOMER, SKY_SOCIAL_JOB } from '../../constants/skySocialMedia'
import type { AutoCoderEntity, EntityPatchRequest } from '../../types/gotypes'

/**
 * Post data — find one post and correct what it says (#1196).
 *
 * 🔴 WHY THIS PAGE EXISTS, AND WHY IT IS NOT A NICE-TO-HAVE. Sky's U35 figures
 * are GONE from production: no `u35` under any spelling, on any entity or
 * pipeline. This screen and the CSV upload are the only two routes by which they
 * come back. The way they were lost is the design brief (#1194) — a number filed
 * as a text label is invisible to every numeric read, and two spellings of one
 * metric name are two metrics nothing can reconcile — so the panel below refuses
 * both by construction rather than by asking the user to be careful.
 *
 * THE PRIMARY JOURNEY IS ONE SENTENCE, and the layout is built around it:
 * "paste a URL of a social media post, find that entity". So the paste box is
 * the first thing on the page, above the filters, and a link that matches
 * exactly one post opens its editor immediately rather than showing a list of
 * one.
 *
 * ⚠ IT IS JOB-WIDE, NOT PER-DEPARTMENT, and that is deliberate rather than an
 * omission. `/entities/query`, `GET /entities/:id` and its PATCH are registered
 * only on the job-wide AutoCoder group — there is no product-scoped version — and
 * that suits the work: which department a post belongs to is an ANSWER this page
 * gives you, not a precondition you have to supply before pasting a link. See
 * `skyJobURL` for the boundary that keeps this exception narrow.
 */

/**
 * ⚠ `gotypes.ts` IS STALE FOR THIS STRUCT. `types.AutoCoderEntity` carries
 * `campaignId` and `campaignName` (goapi/pkg/types/autocoder.go) and the JSON
 * on the wire carries both, but the generated TypeScript does not list them.
 * Widened here rather than hand-edited into the generated file, which would put
 * the generator and the checkout permanently out of step. Regenerating gotypes
 * is the real fix and belongs with whoever owns the Go side.
 */
type EntityRow = AutoCoderEntity & { campaignId?: string; campaignName?: string }

/** UTC days, matching every other timestamp in the AutoCoder surfaces. */
function formatDay(value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/**
 * What to call a post. `name` is the connector's title for it; one that arrived
 * without a title is still identifiable by the id the platform knows it by,
 * which is the thing an operator would have pasted.
 */
function postName(entity: EntityRow): string {
  return entity.name?.trim() || entity.externalId || entity.id
}

const ROWS_PER_PAGE = 25

export const PostDataEditor: React.FC = () => {
  // The paste box, and the value the LOOKUP actually runs on. Two states, not
  // one, because they are answers to different questions: what is in the box,
  // and what the page is currently showing the results of.
  const [typedUrl, setTypedUrl] = React.useState('')
  const [lookupUrl, setLookupUrl] = React.useState('')

  const [campaigns, setCampaigns] = React.useState<string[]>([])
  const [from, setFrom] = React.useState('')
  const [to, setTo] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [clickedId, setClickedId] = React.useState<string | undefined>(undefined)
  // Closing an auto-opened dialog has to stick: the single match that opened it
  // is still the result, so without this it would reopen on the next render.
  const [autoDismissed, setAutoDismissed] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [savedAt, setSavedAt] = React.useState<number | null>(null)

  // The job's own campaigns, metric names and label keys. Job-wide (no
  // `product`), because this page is: the metric names it offers must be every
  // name in use, not one department's.
  const summary = useEntityFilterSummary({
    customer: SKY_SOCIAL_CUSTOMER,
    job: SKY_SOCIAL_JOB,
  })

  const publishedDateRange = React.useMemo(() => {
    if (from === '' && to === '') return null
    return { from: from || undefined, to: to || undefined }
  }, [from, to])

  const search = useEntitySearch({
    url: lookupUrl,
    campaigns,
    publishedDateRange,
    page,
    rowsPerPage: ROWS_PER_PAGE,
  })

  // The filter the list is showing, built by the SAME function the search uses,
  // so the spreadsheet download matches the screen rather than a second guess
  // at it.
  const exportQuery = React.useMemo(
    () => buildEntitySearchQuery({ url: lookupUrl, campaigns, publishedDateRange }),
    [lookupUrl, campaigns, publishedDateRange],
  )

  const entities = search.entities as EntityRow[]
  const isLookup = lookupUrl.trim() !== ''

  /**
   * 🔴 ONE MATCH IS THE ANSWER, SO THE PAGE GIVES THE ANSWER. "Paste a URL, find
   * that entity" is finished when the entity is on screen; a list of one that
   * still has to be clicked is a step that exists only because the page could
   * not decide.
   *
   * DERIVED, NOT STORED. Auto-selection as state would need an effect to keep it
   * in step with the results, and that effect would race the results it depends
   * on — showing the previous post's editor over the new post's row for a render.
   * On a screen whose next action is an overwrite, that is the one bug worth
   * designing out rather than testing for.
   *
   * ⚠ Only for a URL LOOKUP. A filter that happens to narrow to one post has not
   * identified anything — the user was browsing, and opening an editor they did
   * not ask for invites an edit to a post they had not chosen.
   */
  const autoSelectedId =
    isLookup && !autoDismissed && !search.isFetching && entities.length === 1 ? entities[0].id : undefined
  const selectedId = clickedId ?? autoSelectedId

  const detail = useEntityDetail(selectedId)
  const patchMutation = useEntityPatch()

  const runLookup = React.useCallback(
    (value: string) => {
      setLookupUrl(value.trim())
      setPage(0)
      // A new lookup is a new question; keeping the previous answer's editor open
      // beneath it is how somebody edits the post they were looking at before.
      setClickedId(undefined)
      setAutoDismissed(false)
      setSaveError(null)
      setSavedAt(null)
    },
    [],
  )

  const openEditor = (id: string) => {
    setClickedId(id)
    setSaveError(null)
    // The "Saved" chip belongs to the post it was saved on, not the next one.
    setSavedAt(null)
  }

  const closeEditor = () => {
    setClickedId(undefined)
    setAutoDismissed(true)
    setSaveError(null)
    setSavedAt(null)
  }

  const onSave = (patch: EntityPatchRequest) => {
    if (!selectedId) return
    setSaveError(null)
    patchMutation.mutate(
      { id: selectedId, patch },
      {
        onSuccess: () => setSavedAt(Date.now()),
        // The server's 400 NAMES the offending key ("label \"u35\" must be a
        // string…"). That sentence is the only thing on screen that tells the
        // user what to do about it.
        onError: (error) => setSaveError((error as Error).message),
      },
    )
  }

  const selectedRow = entities.find((e) => e.id === selectedId)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', bgcolor: 'background.default' }}>
      {/* Post data IS the Admin tab (Kai, 2026-09-10), so the strip sits above
          it with Admin selected — the way back to the dashboards. Only admins
          are offered the tab; the server refuses this page's writes and
          exports to anyone else. */}
      <ProductTabs product={ADMIN_TAB} />
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <PageHeader
          title="Post data"
          gradient={BRAND_GRADIENT}
          actions={<PostDataCsvActions query={exportQuery} />}
          meta={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Find a post by its link and correct the numbers and labels held against it.
            </Typography>
          }
        />
        <AdminViewTabs view="post-data" />

        {/* ── THE PASTE BOX, FIRST ────────────────────────────────────────────
            Above the filters, deliberately. Kai's framing of the journey is
            "paste a URL of a social media post, find that entity", and a box
            that has to be scrolled to, or found beside four other controls, is
            not the fastest path to anything. */}
        <Box sx={{ px: 3, pt: 3 }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Paste a post link
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                size="medium"
                placeholder="https://www.tiktok.com/@hellosky/video/…"
                value={typedUrl}
                onChange={(e) => setTypedUrl(e.target.value)}
                /* 🔴 ON PASTE AND ON ENTER — NEVER ON EVERY KEYSTROKE. A link
                   typed character by character would fire a query per character,
                   each one a partial URL matching nothing, against an endpoint
                   whose whole value is that it is two INDEXED equality arms
                   rather than a substring scan. A paste is one event, and
                   pasting is what this box is for. */
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text')
                  if (pasted) {
                    e.preventDefault()
                    setTypedUrl(pasted)
                    runLookup(pasted)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runLookup(typedUrl)
                }}
                slotProps={{
                  htmlInput: { 'data-testid': 'post-data-url-input' },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ flex: '1 1 420px' }}
              />
              <Button
                color="info"
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={() => runLookup(typedUrl)}
                data-testid="post-data-find"
              >
                Find
              </Button>
              {isLookup && (
                <Button
                  color="info"
                  size="large"
                  onClick={() => {
                    setTypedUrl('')
                    runLookup('')
                  }}
                  data-testid="post-data-clear-url"
                >
                  Clear
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Share-sheet links are fine — tracking parameters, a missing “https://”, “www.” and a
              trailing slash are all handled.
            </Typography>
          </Paper>
        </Box>

        {/* ── FILTERS ─────────────────────────────────────────────────────────
            Second, because they answer the other question: "I do not have a
            link, show me the posts in this campaign between these dates". */}
        <Box sx={{ px: 3, pt: 2, display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* The SAME campaign control the dashboards use, so a campaign is
              named and counted identically wherever it appears. Its options are
              the job's, not one department's — this page is job-wide. */}
          <CampaignFilter
            campaigns={summary.campaigns}
            selected={campaigns}
            onChange={(next) => {
              setCampaigns(next)
              setPage(0)
            }}
            disabled={search.isFetching}
          />
          <TextField
            size="small"
            type="date"
            label="Published from"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value)
              setPage(0)
            }}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'post-data-from' } }}
          />
          <TextField
            size="small"
            type="date"
            label="Published to"
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setPage(0)
            }}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'post-data-to' } }}
          />
        </Box>

        {search.error && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
            {search.error.message}
          </Alert>
        )}

        {/* 🔴 A MISS READS AS A MISS. The lookup deliberately does not fall back
            to a substring search — the server records the accepted gap and its
            reason: "an honest miss beats a wrong row when the next step is a
            human overwriting data". An empty table under a pasted link would
            read as "there are no posts", which is a different and wrong claim. */}
        {isLookup && !search.isFetching && entities.length === 0 && (
          <Alert severity="warning" sx={{ mx: 3, mt: 2 }} data-testid="post-data-no-match">
            No post matches that link. Check it is a link to the post itself rather than to a profile
            or a search — and note that a post only appears here once it has been imported.
          </Alert>
        )}

        <SectionHeader
          title={isLookup ? 'Matching post' : 'Posts'}
          subtitle={
            search.isLoading
              ? 'Loading…'
              : `${search.totalCount.toLocaleString()} post${search.totalCount === 1 ? '' : 's'}`
          }
        />

        <Box sx={{ px: 3 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Post</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Published</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entities.map((entity) => (
                    <TableRow
                      key={entity.id}
                      hover
                      selected={entity.id === selectedId}
                      data-testid={`post-data-row-${entity.id}`}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {postName(entity)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {entity.externalId}
                        </Typography>
                      </TableCell>
                      <TableCell>{entity.platform || '—'}</TableCell>
                      {/* The campaign NAME. Its id ("2531_10050") identifies
                          nothing to a human deciding whether this is the post
                          they meant, and choosing the wrong post to overwrite is
                          the worst failure available on this page. */}
                      <TableCell>{entity.campaignName || entity.campaignId || '—'}</TableCell>
                      <TableCell>{formatDay(entity.publishedDate)}</TableCell>
                      <TableCell align="right">
                        <Button
                          color="info"
                          variant="outlined"
                          size="small"
                          startIcon={<EditOutlinedIcon fontSize="small" />}
                          onClick={() => openEditor(entity.id)}
                          data-testid={`post-data-edit-${entity.id}`}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {entities.length === 0 && !search.isLoading && !isLookup && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No posts match these filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            {search.totalCount > ROWS_PER_PAGE && (
              <TablePagination
                component="div"
                count={search.totalCount}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
              />
            )}
          </Paper>
        </Box>

        {/* ── THE EDITOR, IN A DIALOG ─────────────────────────────────────────
            Over the list rather than under it: below a 25-row table the form
            was off-screen and it was unclear which post it belonged to. The
            dialog names the post in its title.
            A backdrop click does NOT close it — one stray click would throw
            away typed numbers. The close button and Escape do. */}
        <Dialog
          open={Boolean(selectedId)}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') closeEditor()
          }}
          fullWidth
          maxWidth="md"
          slotProps={{ paper: { 'data-testid': 'post-data-edit-dialog' } as object }}
        >
          <DialogTitle sx={{ pr: 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                {selectedRow ? postName(selectedRow) : 'Post'}
              </Typography>
              {/* A save that changed nothing on screen is indistinguishable
                  from a save that did not happen, so it says so. */}
              {savedAt !== null && !patchMutation.isPending && (
                <Chip
                  label="Saved"
                  color="success"
                  size="small"
                  data-testid="post-data-saved"
                  sx={{ height: 20 }}
                />
              )}
            </Box>
            {selectedRow?.url && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ wordBreak: 'break-all', fontWeight: 400 }}
              >
                {selectedRow.url}
              </Typography>
            )}
            <IconButton
              aria-label="Close"
              onClick={closeEditor}
              data-testid="post-data-edit-close"
              sx={{ position: 'absolute', right: 12, top: 12 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {detail.error ? (
              <Alert severity="error">{detail.error.message}</Alert>
            ) : detail.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <EntityEditPanel
                detail={detail.detail}
                knownMetrics={summary.metrics}
                knownLabelKeys={Object.keys(summary.labels ?? {}).sort()}
                onSave={onSave}
                saving={patchMutation.isPending}
                saveError={saveError}
                savedAt={savedAt}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  )
}

export default PostDataEditor
